import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import LessonLayout from "./LessonLayout";

const fmt = (n) => `$${Math.round(n).toLocaleString()}`;

export default function LearnEmergencyFund() {
  const [monthlyExpenses, setMonthlyExpenses] = useState(2500);
  const target3 = monthlyExpenses * 3;
  const target6 = monthlyExpenses * 6;

  return (
    <LessonLayout
      meta={{
        number: 2,
        slug: "emergency-fund",
        time: "4 min read",
        title: "Emergency fund — your first savings goal.",
        hook: "Before you buy a stock, open a Roth IRA, or think about retirement — build this. It's boring, and it's the single most important financial move you'll make.",
        quickAnswer:
          "Before retirement, keep three to six months of essential expenses in a high-yield savings account you never invest. In retirement, hold one to two years of spending in cash or short-term bonds — that buffer lets you avoid selling stocks during a crash, which is the main defense against sequence-of-returns risk.",
        seoDescription: "Interactive lesson on why an emergency fund is your first savings goal. Calculate your target and understand why it comes before investing.",
        bookCategory: "saving",
      }}
      next={{ slug: "first-roth", title: "Your first Roth IRA" }}
    >
      <div className="bg-white border border-[#E5E2DC] rounded-2xl p-6">
        <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold">
          Your monthly expenses (rent, food, transportation, bills)
        </Label>
        <div className="mt-1.5 flex items-center bg-[#F9F8F6] border border-[#E5E2DC] rounded-lg w-fit">
          <span className="pl-3 text-[#5C6B62] text-sm">$</span>
          <Input
            data-testid="learn-monthly-expenses"
            type="number"
            value={monthlyExpenses}
            onChange={(e) => setMonthlyExpenses(Number(e.target.value) || 0)}
            className="border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 font-mono-num text-lg py-2 w-32"
          />
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4">
          <div className="bg-[#F0EFEA] rounded-xl p-5">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold mb-1">
              Starter (3 months)
            </div>
            <div className="font-mono-num text-2xl text-[#1C2B23]">{fmt(target3)}</div>
            <div className="text-xs text-[#5C6B62] mt-2">
              What you need if you have a stable job.
            </div>
          </div>
          <div className="bg-[#E8F0EB] border border-[#2B5339] rounded-xl p-5">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#2B5339] font-semibold mb-1">
              Full (6 months)
            </div>
            <div className="font-mono-num text-2xl text-[#2B5339]">{fmt(target6)}</div>
            <div className="text-xs text-[#1C2B23] mt-2">
              What you need if your income is variable (freelance, gig work, small biz).
            </div>
          </div>
        </div>
      </div>

      <div className="prose prose-sm max-w-none text-[#1C2B23] space-y-4 leading-relaxed">
        <h2 className="text-2xl font-medium">Why this before investing</h2>
        <p>
          Life will hit you with a $2,000 car repair, a broken laptop, a medical bill, or a job loss. Everyone.
          If you don&apos;t have cash, you put it on a credit card at 24% interest and now you&apos;re in a hole
          that eats years of your progress.
        </p>
        <p>
          An emergency fund isn&apos;t about investing. It&apos;s about <strong>not being forced to sell
          investments at the worst possible time</strong>. That&apos;s worth more than any stock pick.
        </p>

        <h2 className="text-2xl font-medium mt-8">Where to keep it</h2>
        <p>
          High-yield savings account. Not a checking account (too easy to spend). Not stocks (too volatile —
          the whole point is that it doesn&apos;t drop when you need it). Not crypto (see previous).
        </p>
        <p>
          As of {new Date().getFullYear()}, HYSA rates range from 4% to 5%. Google &ldquo;best high-yield
          savings account&rdquo; and pick one from a reputable name (Marcus, Ally, Wealthfront, Discover).
        </p>

        <h2 className="text-2xl font-medium mt-8">How to build it fast</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li>Set up an automatic transfer of even $50/month the day you get paid.</li>
          <li>Every &ldquo;raise&rdquo; or tax refund → straight into the emergency fund until you hit your target.</li>
          <li>Sell things you don&apos;t use. That old iPad, that guitar you never play.</li>
          <li>Take a side gig for 3 months. Uber, dog walking, tutoring — pick one and grind.</li>
        </ul>

        <p className="text-[#2B5339] font-medium mt-6">
          When you have this money, everything about your relationship to work, risk, and money changes.
          You can say no. You can wait for the right thing. Anxiety about surprise bills disappears. It&apos;s the
          most underrated financial move of your life.
        </p>
      </div>
    </LessonLayout>
  );
}
